/**
 * RorosSidebarLayout — J3-A
 *
 * ROROS-specific sidebar layout that extends the boilerplate BaseLayout with
 * a dynamic "Open work" badge on the Control Tower nav entry.
 *
 * The badge shows the count of work items in backlog | in_progress | quality_confirmation
 * and auto-refreshes via TanStack Query's staleTime polling.
 *
 * Rule #0 compliance: this component lives in src/components/roros/ (not in boilerplate).
 * AppRouter.tsx uses this component for all /roros/* routes instead of
 * <SidebarLayout type="roros">.
 *
 * PRD §12.3.6 task 2 (sidebar badge).
 */

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BaseLayout } from '@/components/Layout/BaseLayout';
import type { NavSection } from '@/components/Layout/BaseLayout';
import { rorosLayoutConfig } from '@/routes/roros/layout.config';
import { useOperatingMode } from '@/hooks/roros/useOperatingMode';
import { AiBusyOverlay } from '@/components/AiBusyOverlay';
import { fetchJson } from '@/services/roros/base-roros.service';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { useAiConfigStatus } from '@/hooks/roros/useAiConfigStatus';
import { t } from '@/lib/i18n';

// ─── Badge computation ─────────────────────────────────────────────────────

/**
 * AC-inbox-17: Returns the count of ALL open Inbox items (all three kinds).
 * Re-pointed from the old cascade-only /verification-inbox?state=pending endpoint
 * to the unified /inbox?status=open endpoint so QC + Decision + Cascade are
 * all counted. Badge decrements automatically when any action invalidates the
 * inbox query (useRorosInboxQcTransition/DecisionApprove/etc. all invalidate
 * ['roros', 'inbox']).
 *
 * Gated on roros.control-tower.view (same as the Inbox page route guard).
 * Returns undefined when count is 0 (hides the badge).
 */
function useVerificationInboxBadge(): string | undefined {
  const { hasPermission } = usePermissions();
  const canView = hasPermission(PERMISSIONS.ROROS_CONTROL_TOWER_VIEW);

  const { data } = useQuery<{ items: unknown[]; total: number }>({
    queryKey: ['roros', 'inbox', 'badge'],
    // BUG-P2-1 fix: use fetchJson (mirrors the list hook) so the response
    // envelope { success, data: { items, total } } is unwrapped to { items, total }.
    // Raw res.json() returns the full envelope, making data.total / data.items
    // undefined → count 0 → badge never rendered.
    queryFn: () =>
      fetchJson<{ items: unknown[]; total: number }>('/api/v1/inbox?status=open&limit=999').catch(
        () => ({ items: [], total: 0 }),
      ),
    staleTime: 30_000,
    enabled: canView,
  });
  // Use total if available, otherwise fall back to items length
  const count = data?.total ?? data?.items?.length ?? 0;
  return count > 0 ? String(count) : undefined;
}

// ─── RorosSidebarLayout ────────────────────────────────────────────────────

interface RorosSidebarLayoutProps {
  children: React.ReactNode;
  fullHeight?: boolean;
}

// J6 — Nav entries that are gated to Growth/Scaling operating modes. They are
// stripped from the sidebar in Founder mode. Direct URL navigation also 404s
// via the GrowthGate route wrapper, so this is defence-in-depth.
// Dogfood-fix-wave-1 F4: Initiatives is now visible to all founders from day one.
// Only Scorecard remains gated to Growth+ mode.
const GROWTH_ONLY_HREFS = new Set<string>([
  '/roros/dashboards/scorecard',
]);

/** AC-BYOK-9: Returns true when the org has zero enabled AI providers. */
function useNoProviderAttention(): boolean {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ROROS_SETTINGS_MANAGE)
    || hasPermission(PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS);

  const { hasAnyEnabledProvider, isLoading } = useAiConfigStatus();

  // Only show the indicator to users who can actually fix the problem.
  if (!canManage) return false;
  // While loading, don't flash the dot.
  if (isLoading) return false;
  return !hasAnyEnabledProvider;
}

export function RorosSidebarLayout({ children, fullHeight }: RorosSidebarLayoutProps) {
  const inboxBadge = useVerificationInboxBadge();
  const noProviderAttention = useNoProviderAttention();
  const { isAtLeastGrowth, isLoading: modeLoading } = useOperatingMode();

  // Inject the Verification Inbox badge into the nav. Also strip Growth-only
  // items in Founder mode (or before the mode is known, to avoid a brief flash).
  const noProviderAttentionTitle = t('No AI provider configured — add a provider key to unlock AI features');

  const navSections: NavSection[] = useMemo(() => {
    return (rorosLayoutConfig.navSections ?? []).map((section) => ({
      ...section,
      items: section.items
        .filter((item) => {
          if (!GROWTH_ONLY_HREFS.has(item.href)) return true;
          // Hide while loading to prevent a flash, then show only if Growth+.
          return !modeLoading && isAtLeastGrowth;
        })
        .map((item) => {
          // AC-inbox-17: badge now counts ALL open inbox items via unified /inbox endpoint
          if (item.href === '/roros/inbox') return { ...item, badge: inboxBadge };
          // AC-BYOK-9: attention dot on System Settings nav item when zero enabled providers
          if (item.href === '/roros/settings') {
            return {
              ...item,
              attentionDot: noProviderAttention,
              attentionTitle: noProviderAttention ? noProviderAttentionTitle : undefined,
            };
          }
          return item;
        }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxBadge, isAtLeastGrowth, modeLoading, noProviderAttention, noProviderAttentionTitle]);

  return (
    <BaseLayout
      navSections={navSections}
      sidebarTitle={rorosLayoutConfig.sidebarTitle}
      headerSubtitle={rorosLayoutConfig.headerSubtitle}
      rootPath={rorosLayoutConfig.rootPath}
      fullHeight={fullHeight}
    >
      {/* F8 dogfood-fix-wave-1: global wait cursor during AI/LLM calls */}
      <AiBusyOverlay />
      {children}
    </BaseLayout>
  );
}
